import React, { useState, useEffect, useRef } from "react";
import "../styles/InventoryUpdate.css";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { graphqlRequest } from "../../../utils/graphqlRequest";

function isIncomingAction(action) {
  const a = String(action || '').toLowerCase();
  return a.includes('add to stock') || a.includes('return');
}

function usesPriorAssignees(action) {
  const a = String(action || '').toLowerCase();
  return a.includes('adjust') || a.includes('return');
}

function isIssueLikeAction(action) {
  const a = String(action || '').toLowerCase();
  if (!a || a.includes('add to stock') || a.includes('return') || a.includes('adjust')) {
    return false;
  }
  return true;
}

const FIND_ITEM_ASSIGNMENTS = `
  query FindItemAssignments($limit: Int) {
    findAllInventoryManagement(limit: $limit) {
      id
      tool { id }
      quantityAssigned
      assignedBy
      updateAction
    }
  }
`;

const CREATE_INVENTORY_MANAGEMENT = `
  mutation CreateInventoryManagement($createInventoryManagementInput: CreateInventoryManagementInput!) {
    createInventoryManagement(createInventoryManagementInput: $createInventoryManagementInput) {
      id
      tool {
        id
        name
        code
        quantity
        available_quantity
      }
      project {
        id
        name
      }
      quantityAssigned
      assignedAt
      assignedBy
      category
      remark
      updateAction
    }
  }
`;

const UPDATE_INVENTORY_MANAGEMENT = `
  mutation UpdateInventoryManagement($updateInventoryManagementInput: UpdateInventoryManagementInput!) {
    updateInventoryManagement(updateInventoryManagementInput: $updateInventoryManagementInput) {
      id
      tool {
        id
        name
        code
        quantity
        available_quantity
      }
      project {
        id
        name
      }
      quantityAssigned
      assignedAt
      assignedBy
      category
      remark
      updateAction
    }
  }
`;

export default function InventoryUpdate({
  onClose,
  onInventoryAdded,
  items = [],
  projects = [],
  editData = null,
  defaultCategory = '',
  defaultUpdateAction = 'Issued to Staff',
  staffOptions = [],
  modalTitle = 'Inventory Update',
  lockAction = false,
}) {
  const [formData, setFormData] = useState({
    itemCode: '',
    itemName: '', // Read-only, auto-filled from lookup
    itemId: '', // Store the found item ID
    quantity: '',
    quantityUnit: 'Pcs',
    updateAction: defaultUpdateAction || 'Issued to Staff',
    supplier: '',
    projectId: '',
    projectName: '',
    category: defaultCategory || '',
    remark: '',
    assignedTo: '',
  });
  const [itemCodeError, setItemCodeError] = useState('');
  const [quantityError, setQuantityError] = useState('');
  const [isLookingUpItem, setIsLookingUpItem] = useState(false);
  const [lookupDisplay, setLookupDisplay] = useState({ label: '', value: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [catalogItems, setCatalogItems] = useState(Array.isArray(items) ? items : []);
  const [projectList, setProjectList] = useState(Array.isArray(projects) ? projects : []);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState('');
  const [availableQty, setAvailableQty] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const blurHideRef = useRef(null);
  const [priorAssignees, setPriorAssignees] = useState([]);
  const [assigneesLoading, setAssigneesLoading] = useState(false);

  // Keep local catalog in sync with parent list when provided
  useEffect(() => {
    if (Array.isArray(items) && items.length > 0) {
      setCatalogItems(items);
    }
  }, [items]);

  useEffect(() => {
    if (Array.isArray(projects) && projects.length > 0) {
      setProjectList(projects);
    }
  }, [projects]);

  // Prefill category / action when opened from Uniform & Equipment tabs
  useEffect(() => {
    if (editData) return;
    setFormData((prev) => ({
      ...prev,
      category: defaultCategory || prev.category,
      updateAction: defaultUpdateAction || prev.updateAction,
    }));
  }, [defaultCategory, defaultUpdateAction, editData]);

  // Always load projects in the modal (parent list can be empty / fail silently)
  useEffect(() => {
    let cancelled = false;
    const loadProjects = async () => {
      setProjectsLoading(true);
      setProjectsError('');
      try {
        const data = await graphqlRequest(`
          query FindAllProjects {
            findAllProject {
              id
              name
            }
          }
        `);
        if (cancelled) return;
        const rows = Array.isArray(data?.findAllProject) ? data.findAllProject : [];
        setProjectList(rows);
        if (!rows.length) {
          setProjectsError('No projects found. Create a project first.');
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        if (!cancelled) {
          setProjectsError(error?.message || 'Failed to load projects');
        }
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    };
    loadProjects();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadPriorAssignees = async () => {
      if (!formData.itemId || !usesPriorAssignees(formData.updateAction)) {
        setPriorAssignees([]);
        return;
      }
      setAssigneesLoading(true);
      try {
        const data = await graphqlRequest(FIND_ITEM_ASSIGNMENTS, {
          limit: 400,
        });
        if (cancelled) return;
        const rows = Array.isArray(data?.findAllInventoryManagement)
          ? data.findAllInventoryManagement
          : [];
        const byName = {};
        rows
          .filter((rec) => rec?.tool?.id === formData.itemId)
          .forEach((rec) => {
            const name = String(rec.assignedBy || '').trim();
            if (!name) return;
            const qty = Number(rec.quantityAssigned) || 0;
            if (!byName[name]) byName[name] = 0;
            if (isIssueLikeAction(rec.updateAction)) byName[name] += qty;
            else if (String(rec.updateAction || '').toLowerCase().includes('return')) {
              byName[name] -= qty;
            }
          });
        const list = Object.entries(byName)
          .map(([name, qty]) => ({ name, qty }))
          .filter((row) => row.qty > 0)
          .sort((a, b) => a.name.localeCompare(b.name));
        setPriorAssignees(list);
        if (list.length === 1) {
          setFormData((prev) =>
            prev.assignedTo ? prev : { ...prev, assignedTo: list[0].name },
          );
        }
      } catch {
        if (!cancelled) setPriorAssignees([]);
      } finally {
        if (!cancelled) setAssigneesLoading(false);
      }
    };
    void loadPriorAssignees();
    return () => {
      cancelled = true;
    };
  }, [formData.itemId, formData.updateAction]);

  // Populate form if editing
  useEffect(() => {
    if (editData) {
      setFormData({
        itemCode: editData.code || editData.item || '',
        itemName: editData.item || '',
        itemId: editData.itemId || '',
        quantity: editData.quantity?.replace(/\s*(pcs|ltr|box)/i, '') || '',
        quantityUnit: editData.quantity?.match(/(pcs|ltr|box)/i)?.[0] || 'Pcs',
        updateAction: editData.updateAction || 'Add to Stock',
        supplier: '',
        projectId: editData.projectId || '',
        projectName: editData.project || '',
        category:
          editData.category && editData.category !== '—'
            ? editData.category
            : '',
        remark: editData.remark || '',
        assignedTo: editData.assignedBy || editData.assignedTo || '',
      });
      if (editData.item && editData.code) {
        setLookupDisplay({
          label: 'Matched item',
          value: `${editData.item} (${editData.code})`,
        });
      }
    }
  }, [editData]);

  const applyFoundItem = (foundItem, matchedBy) => {
    const inputValue =
      matchedBy === 'code'
        ? foundItem.code || foundItem.name || ''
        : foundItem.name || foundItem.code || '';

    const listedQty = Number(foundItem.quantity);
    const availRaw = foundItem.available_quantity;
    const listed = Number.isFinite(listedQty) ? listedQty : 0;
    const onHand =
      availRaw === null || availRaw === undefined
        ? listed
        : Math.max(0, Number(availRaw) || 0);

    setFormData((prev) => ({
      ...prev,
      itemCode: inputValue,
      itemName: foundItem.name || '',
      itemId: foundItem.id,
    }));
    setAvailableQty(onHand);
    setLookupDisplay({
      label: 'Selected item',
      value: `${foundItem.name || 'Unnamed'}${
        foundItem.code ? ` (${foundItem.code})` : ''
      } · ${onHand} remaining in Item Description`,
    });
    setItemCodeError('');
    setQuantityError('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const rankMatches = (allItems, input) => {
    const q = String(input || '').toLowerCase().trim();
    if (!q) return [];
    return allItems
      .map((item) => {
        const name = String(item.name || '').toLowerCase();
        const code = String(item.code || '').toLowerCase();
        let score = 0;
        if (code === q) score = 100;
        else if (name === q) score = 90;
        else if (code.startsWith(q)) score = 70;
        else if (name.startsWith(q)) score = 60;
        else if (code.includes(q)) score = 40;
        else if (name.includes(q)) score = 30;
        else return null;
        return { item, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score || String(a.item.name).localeCompare(String(b.item.name)))
      .slice(0, 8)
      .map((row) => row.item);
  };

  const ensureCatalog = async () => {
    if (catalogItems.length) return catalogItems;
    const allItemsData = await graphqlRequest(`
      query FindAllItems {
        findAllItem {
          id
          name
          code
          quantity
          available_quantity
        }
      }
    `);
    const allItems = allItemsData?.findAllItem || [];
    setCatalogItems(allItems);
    return allItems;
  };

  // Exact SKU/name can lock in; similar names stay as a pick-list.
  const lookupItem = async (input, { autoSelectExact = true } = {}) => {
    if (!input || !input.trim()) {
      setFormData((prev) => ({ ...prev, itemName: '', itemId: '' }));
      setLookupDisplay({ label: '', value: '' });
      setItemCodeError('');
      setAvailableQty(null);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLookingUpItem(true);
    setItemCodeError('');

    try {
      const allItems = await ensureCatalog();

      if (!allItems.length) {
        setFormData((prev) => ({ ...prev, itemName: '', itemId: '' }));
        setLookupDisplay({ label: '', value: '' });
        setSuggestions([]);
        setShowSuggestions(false);
        setItemCodeError(
          'No items in catalog yet. Add items first under Resources → Products.',
        );
        return;
      }

      const trimmed = input.toLowerCase().trim();
      const matches = rankMatches(allItems, input);
      setSuggestions(matches);
      setShowSuggestions(matches.length > 0);

      const exactCodeMatches = allItems.filter(
        (item) => item.code?.toLowerCase().trim() === trimmed,
      );
      const exactNameMatches = allItems.filter(
        (item) => item.name?.toLowerCase().trim() === trimmed,
      );

      if (autoSelectExact && exactCodeMatches.length === 1) {
        applyFoundItem(exactCodeMatches[0], 'code');
        return;
      }
      if (autoSelectExact && exactNameMatches.length === 1) {
        applyFoundItem(exactNameMatches[0], 'name');
        return;
      }

      setFormData((prev) => ({ ...prev, itemName: '', itemId: '' }));
      setLookupDisplay({ label: '', value: '' });
      setAvailableQty(null);

      if (!matches.length) {
        setItemCodeError(
          'No matching product. Type more of the name or SKU, then pick from the list.',
        );
      }
    } catch (error) {
      console.error('Error looking up item by code:', error);
      setItemCodeError('Failed to lookup item. Please try again.');
      setFormData((prev) => ({ ...prev, itemName: '', itemId: '' }));
      setLookupDisplay({ label: '', value: '' });
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLookingUpItem(false);
    }
  };

  // Debounce timer ref
  const debounceTimerRef = useRef(null);

  // Handle item code change with debounce
  const handleItemCodeChange = (e) => {
    const code = e.target.value;
    setFormData((prev) => ({ ...prev, itemCode: code, itemName: '', itemId: '' }));
    setItemCodeError('');
    setQuantityError('');
    setLookupDisplay({ label: '', value: '' });
    setAvailableQty(null);

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounce
    debounceTimerRef.current = setTimeout(() => {
      if (code && code.trim().length >= 2) {
        lookupItem(code, { autoSelectExact: true });
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 250);
  };

  // Handle project selection
  const handleProjectChange = (projectId) => {
    const project = projectList.find((p) => p.id === projectId);
    if (project) {
      setFormData((prev) => ({
        ...prev,
        projectId: project.id,
        projectName: project.name,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        projectId: '',
        projectName: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission (also covers the async stock-check window)
    if (isSubmitting) {
      return;
    }

    // Validation
    if (!formData.itemCode || !formData.itemCode.trim()) {
      toast.error('Please enter an item code');
      return;
    }

    if (!formData.itemId) {
      toast.error('Item code not found. Please enter a valid item code from Item Description.');
      setItemCodeError('Item code not found. Please enter a valid item code from Item Description.');
      return;
    }

    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      setQuantityError('Please enter a valid quantity');
      return;
    }

    const action = String(formData.updateAction || '').toLowerCase();
    const isIncoming =
      action.includes('add to stock') || action.includes('return');
    if (!isIncoming && (!formData.projectId || formData.projectId.trim() === '')) {
      toast.error('Please select a site / project');
      return;
    }

    const requestedQty = parseFloat(formData.quantity);
    setQuantityError('');
    setIsSubmitting(true);

    try {
      // Always re-check on-hand stock from Item Description before save.
      // Fail closed if this check cannot run — never save on stale cache.
      let onHand = NaN;
      try {
        const fresh = await graphqlRequest(`
          query FindAllItemsForStockCheck {
            findAllItem {
              id
              name
              quantity
              available_quantity
            }
          }
        `);
        const row = (fresh?.findAllItem || []).find((i) => i.id === formData.itemId);
        if (!row) {
          setQuantityError(
            'Could not verify Item Description stock. Look up the item again.',
          );
          return;
        }
        const listed = Number(row.quantity) || 0;
        onHand =
          row.available_quantity === null ||
          row.available_quantity === undefined
            ? listed
            : Math.max(0, Number(row.available_quantity) || 0);
        setAvailableQty(onHand);
        setCatalogItems(fresh?.findAllItem || []);
      } catch (err) {
        console.warn('Stock re-check failed', err);
        setQuantityError(
          'Could not verify Item Description stock. Try again.',
        );
        return;
      }

      if (!Number.isFinite(onHand)) {
        setQuantityError(
          'Could not verify Item Description stock. Look up the item again.',
        );
        return;
      }

      // Stock Out / Transfer / Issue cannot exceed on-hand. Stock In can add more.
      if (!isIncoming && requestedQty > onHand) {
        setQuantityError(
          `Item Description only has ${onHand} remaining. Cannot save quantity ${requestedQty}.`,
        );
        return;
      }

      // Use the itemId from the code lookup
      const itemId = formData.itemId || editData?.itemId;

      if (!itemId) {
        toast.error('Item code not found. Please enter a valid item code.');
        return;
      }

      if (editData) {
        // Update existing inventory management record
        // If projectId is empty string, send null to clear the project
        // If projectId is not provided, send undefined to keep existing project
        const projectIdValue = formData.projectId 
          ? formData.projectId 
          : (formData.projectId === '' ? null : undefined);
        
        const updateInput = {
          id: editData.id,
          itemId: itemId,
          projectId: projectIdValue,
          quantityAssigned: parseInt(formData.quantity),
          assignedBy: formData.assignedTo?.trim() || undefined,
          remark: formData.remark || undefined,
          updateAction: formData.updateAction || undefined,
          category: formData.category || undefined,
        };

        await graphqlRequest(UPDATE_INVENTORY_MANAGEMENT, {
          updateInventoryManagementInput: updateInput,
        });

        toast.success('Inventory update updated successfully!');
      } else {
        // Create new inventory management record
        // Project is now required, so we always send it
        const createInput = {
          itemId: itemId,
          projectId: formData.projectId,
          quantityAssigned: parseInt(formData.quantity),
          assignedBy: formData.assignedTo?.trim() || undefined,
          remark: formData.remark || undefined,
          updateAction: formData.updateAction || undefined,
          category: formData.category || undefined,
        };

        await graphqlRequest(CREATE_INVENTORY_MANAGEMENT, {
          createInventoryManagementInput: createInput,
        });

        toast.success('Inventory update created successfully!');
      }

      // Call parent callback to refresh the list
      if (onInventoryAdded) {
        onInventoryAdded();
      }

      // Reset form
      setFormData({
        itemCode: '',
        itemName: '',
        itemId: '',
        quantity: '',
        quantityUnit: 'Pcs',
        updateAction: defaultUpdateAction || 'Issued to Staff',
        supplier: '',
        projectId: '',
        projectName: '',
        category: defaultCategory || '',
        remark: '',
        assignedTo: '',
      });
      setItemCodeError('');
      setQuantityError('');
      setAvailableQty(null);
      onClose();
    } catch (error) {
      console.error('Error saving inventory update:', error);
      const msg = error?.message || 'Failed to save inventory update';
      if (
        /only \d+/i.test(msg) ||
        /cannot save quantity/i.test(msg) ||
        /cannot assign/i.test(msg) ||
        /item description/i.test(msg)
      ) {
        setQuantityError(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="inventory-update-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="inventory-update-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="inventory-update-title"
      >
        {/* Top Nav */}
        <div className="inventory-update-header">
          <h2 id="inventory-update-title" className="inventory-update-title">
            {editData ? 'Edit Inventory Update' : modalTitle || 'Inventory Update'}
          </h2>
          <button
            type="button"
            className="inventory-update-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Form */}
        <form className="inventory-update-form" onSubmit={handleSubmit}>
          {/* Project - Required at top */}
          <div className="inventory-update-row">
            <div className="inventory-update-field">
              <label>
                Site / Project {isIncomingAction(formData.updateAction) ? '' : '*'}
                {formData.category === 'Vehicle'
                  ? ' (destination / job site)'
                  : ''}
              </label>
              <select
                value={formData.projectId}
                onChange={(e) => handleProjectChange(e.target.value)}
                required={!isIncomingAction(formData.updateAction)}
                disabled={projectsLoading || (!projectList.length && !projectsError)}
              >
                <option value="">
                  {projectsLoading
                    ? 'Loading projects…'
                    : projectList.length
                      ? 'Select a site / project'
                      : 'No projects available'}
                </option>
                {projectList.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
              {projectsError && (
                <p style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                  {projectsError}
                </p>
              )}
            </div>
          </div>

          {/* Item Code / Name */}
          <div className="inventory-update-row">
            <div className="inventory-update-field">
              <label>Item Code or Name *</label>
          <div className="inventory-item-lookup">
          <input
            type="text"
            placeholder="Type name or SKU, then select the product"
            value={formData.itemCode}
            onChange={handleItemCodeChange}
            onFocus={() => {
              if (suggestions.length) setShowSuggestions(true);
            }}
            onBlur={() => {
              if (blurHideRef.current) clearTimeout(blurHideRef.current);
              blurHideRef.current = setTimeout(() => {
                setShowSuggestions(false);
              }, 180);
            }}
            autoComplete="off"
            required
            style={{ borderColor: itemCodeError ? 'red' : '' }}
          />
          {showSuggestions && suggestions.length > 0 && (
            <ul className="inventory-item-suggestions" role="listbox">
              {suggestions.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyFoundItem(item, 'name')}
                  >
                    <span className="inventory-item-suggestions-name">
                      {item.name || 'Unnamed'}
                    </span>
                    <span className="inventory-item-suggestions-meta">
                      {item.code || 'No SKU'}
                      {item.available_quantity != null
                        ? ` · ${item.available_quantity} on hand`
                        : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          </div>
              {itemCodeError && (
                <p style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{itemCodeError}</p>
              )}
              {isLookingUpItem && (
                <p style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>Looking up item...</p>
              )}
              {showSuggestions && suggestions.length > 1 && !formData.itemId && (
                <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                  Several products match. Select the one you want from the list.
                </p>
              )}
              {lookupDisplay.value && !itemCodeError && (
                <div style={{ 
                  marginTop: '8px', 
                  padding: '8px 12px', 
                  backgroundColor: '#e8f5e9', 
                  borderRadius: '4px',
                  border: '1px solid #4caf50'
                }}>
                  <p style={{ 
                    color: '#2e7d32', 
                    fontSize: '14px', 
                    margin: 0,
                    fontWeight: '500'
                  }}>
                    <strong>{lookupDisplay.label}:</strong> {lookupDisplay.value}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Quantity */}
          <div className="inventory-update-row">
            <div className="inventory-update-field">
              <label>Quantity *</label>
              <input
                type="number"
                placeholder="0"
                min="0"
                step="0.01"
                value={formData.quantity}
                onChange={(e) => {
                  setQuantityError('');
                  setFormData({ ...formData, quantity: e.target.value });
                }}
                required
                style={{ borderColor: quantityError ? 'red' : '' }}
              />
              {quantityError && (
                <p style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                  {quantityError}
                </p>
              )}
              {availableQty != null && !quantityError && (
                <p style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                  {isIncomingAction(formData.updateAction)
                    ? `Current on-hand: ${availableQty}. This will add to Item Description stock.`
                    : `Remaining in Item Description: ${availableQty}. Stock Out cannot exceed this.`}
                </p>
              )}
            </div>
          </div>

          {/* Assigned to — for adjustments, choose who currently holds the item */}
          <div className="inventory-update-row">
            <div className="inventory-update-field">
              <label>
                {usesPriorAssignees(formData.updateAction)
                  ? 'Assigned to (who currently holds this item)'
                  : 'Assigned to (employee / manager)'}
              </label>
              {usesPriorAssignees(formData.updateAction) ? (
                <>
                  <select
                    value={formData.assignedTo}
                    onChange={(e) =>
                      setFormData({ ...formData, assignedTo: e.target.value })
                    }
                    disabled={!formData.itemId || assigneesLoading}
                  >
                    <option value="">
                      {!formData.itemId
                        ? 'Select a product first'
                        : assigneesLoading
                          ? 'Loading previous assignments…'
                          : priorAssignees.length
                            ? 'Select who it was assigned to'
                            : 'No prior assignments for this product'}
                    </option>
                    {priorAssignees.map((row) => (
                      <option key={row.name} value={row.name}>
                        {row.name} · {row.qty} still issued
                      </option>
                    ))}
                    {formData.assignedTo &&
                    !priorAssignees.some((row) => row.name === formData.assignedTo) ? (
                      <option value={formData.assignedTo}>{formData.assignedTo}</option>
                    ) : null}
                  </select>
                  <p style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                    Lists staff this product was issued to (Stock Out). Pick the person this
                    adjustment or return is for.
                  </p>
                </>
              ) : Array.isArray(staffOptions) && staffOptions.length > 0 ? (
                <select
                  value={formData.assignedTo}
                  onChange={(e) =>
                    setFormData({ ...formData, assignedTo: e.target.value })
                  }
                >
                  <option value="">Select staff member</option>
                  {staffOptions.map((u) => {
                    const name =
                      `${u.firstName || ''} ${u.lastName || ''}`.trim() ||
                      u.email;
                    const role = Array.isArray(u.roles)
                      ? u.roles[0]
                      : u.roles || '';
                    return (
                      <option key={u.id} value={name}>
                        {name}
                        {role ? ` · ${role}` : ''}
                        {u.employeeCode ? ` (${u.employeeCode})` : ''}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <input
                  type="text"
                  placeholder="e.g. Jane Doe / Unika Subedi"
                  value={formData.assignedTo}
                  onChange={(e) =>
                    setFormData({ ...formData, assignedTo: e.target.value })
                  }
                />
              )}
              <p style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                Who holds this item (cleaner, manager, or driver).
              </p>
            </div>
          </div>

          {/* Category */}
          <div className="inventory-update-row">
            <div className="inventory-update-field">
              <label>Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <option value="">Select category</option>
                <option>Uniform</option>
                <option>PPE</option>
                <option>Access</option>
                <option>Device</option>
                <option>Vehicle</option>
                <option>Equipment</option>
                <option>Supplies</option>
                </select>
            </div>
          </div>

          {/* Update Action */}
          <div className="inventory-update-row">
            <div className="inventory-update-field">
              <label>Update Action</label>
              <select
                value={formData.updateAction}
                onChange={(e) => setFormData({ ...formData, updateAction: e.target.value })}
                disabled={lockAction}
              >
                <option>Add to Stock</option>
                <option>Issued to Staff</option>
                <option>Returned</option>
                <option>Adjustment</option>
                <option>Transfer</option>
                <option>Marked as Used</option>
                <option>Maintenance</option>
                <option>Dispose</option>
                <option>Damage</option>
              </select>
            </div>
          </div>


          {/* Remark */}
          <div className="inventory-update-field inventory-update-remark">
            <label>
              Remark / Note
              {formData.category === 'Vehicle'
                ? ' (optional: Rego: ABC123)'
                : ''}
            </label>
            <textarea
              placeholder={
                formData.category === 'Vehicle'
                  ? 'Rego: ABC123 · any extra destination notes'
                  : 'Additional Note'
              }
              value={formData.remark}
              onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
            />
          </div>

          {/* Button */}
          <div className="inventory-update-actions">
            <button
              type="button"
              className="inventory-update-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? 'Saving…'
                : editData
                  ? 'Update'
                  : modalTitle?.includes('Stock In')
                    ? 'Save Stock In'
                    : modalTitle?.includes('Stock Out')
                      ? 'Save Stock Out'
                      : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
